/**
 * useFCM — React hook for requesting push notification permission
 * and registering the FCM token with the backend.
 *
 * Call this in TenantLayout or after login to auto-register.
 *
 * Usage:
 *   const { granted, request } = useFCM();
 */
import { useEffect, useRef, useState } from 'react';
import { requestFCMToken, onForegroundMessage } from '@/lib/firebase';
import { toast } from 'sonner';

export function useFCM({ autoRequest = true, showToasts = true } = {}) {
  const [granted, setGranted] = useState(false);
  const [loading, setLoading] = useState(false);
  const registeredRef = useRef(false);

  const request = async () => {
    if (registeredRef.current) return;
    setLoading(true);
    const result = await requestFCMToken();
    setLoading(false);

    if (result.ok) {
      setGranted(true);
      registeredRef.current = true;
    }
    // Silently fail — don't bother user if FCM isn't configured or they denied
    return result;
  };

  // Auto-register on mount (after a short delay so UI loads first)
  useEffect(() => {
    if (!autoRequest) return;
    if (!('Notification' in window)) return;  // not supported
    if (Notification.permission === 'denied') return;  // already denied

    const timer = setTimeout(() => { request(); }, 3000);
    return () => clearTimeout(timer);
  }, [autoRequest]);

  // Handle foreground messages (show toast)
  useEffect(() => {
    let unsubscribe = () => {};
    onForegroundMessage((payload) => {
      const { title, body } = payload.notification || {};
      if (showToasts && (title || body)) {
        toast(title || 'Notification', {
          description: body,
          duration: 6000,
        });
      }
    }).then(fn => { unsubscribe = fn; });
    return () => unsubscribe();
  }, [showToasts]);

  return { granted, loading, request };
}
